import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Grid, Card, CardContent, Typography, TextField,
  Button, MenuItem, Select, FormHelperText, FormControl,
  Alert, Snackbar,
  CircularProgress,
  Stack,
} from "@mui/material";
import { getCampaigns, updateCampaignFollowups } from "../api/apiService";
import { CampaignRounded } from "@mui/icons-material";

export default function FollowUps() {
  const clientId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('clientId')) ?? '';
    } catch {
      return '';
    }
  }, []);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [followUps, setFollowUps] = useState([
    { subject: "", content: "" },
    { subject: "", content: "" },
    { subject: "", content: "" },
  ]);
  const [errors, setErrors] = useState([{}, {}, {}]);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);

  /** Load campaigns data on mount */
  useEffect(() => {

    const fetchCampaigns = async () => {
      if (!clientId) return;
      try {
        setInitLoading(true);
        const [page, pageSize, search] = [0, 0, ""];
        const res = await getCampaigns(clientId, page, pageSize, search);

        // NORMALIZE emailTemplates here
        const normalizedRows = res.data.campaigns.map((c) => ({
          ...c,
          emailTemplates:
            Array.isArray(c.emailTemplates) && c.emailTemplates.length === 3
              ? c.emailTemplates.map((t) =>
                  typeof t === "string"
                    ? { subject: t || "", content: "" }
                    : { subject: t.subject || "", content: t.content || "" }
                )
              : [
                  { subject: "", content: "" },
                  { subject: "", content: "" },
                  { subject: "", content: "" },
                ],
        }));

        setCampaigns(normalizedRows);
        //console.log(res)
      } catch (error) {
        setSnackbar({ open: true, message: 'Not able to fetch campaign details', severity: 'error' });
      } finally {
        setInitLoading(false);
      }
    };

    fetchCampaigns();
  }, [clientId]);

  /* load followup email - subjects and contents */
  const fetchFollowUpData = useCallback(() => {
    try {
      if (selectedId) {
        const selected = campaigns.find(c => c.id === selectedId);
        // Use normalized templates (always 3 objects)
        setFollowUps(selected?.emailTemplates || [
          { subject: "", content: "" },
          { subject: "", content: "" },
          { subject: "", content: "" }
        ]);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Not able to load campaigns', severity: 'error' });
    }
  },[selectedId, campaigns]);

  useEffect(() => {
    fetchFollowUpData();
  }, [fetchFollowUpData]);

  /*on Input change or select */
  const handleInputChange = (i, field, value) => {
    try {
      const updated = [...followUps];
      updated[i][field] = value;
      setFollowUps(updated);

      // Clear error if valid
      if (value.trim() !== "") {
        setErrors(prev => {
          const newErrors = [...prev];
          newErrors[i][field] = "";
          return newErrors;
        });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Not able to update input', severity: 'error' });
    }
  };

  /*validate subjects and contents */
  const validate = () => {
    const newErrors = followUps.map(f => ({
      subject: f.subject.trim() === "" ? "Subject required" : "",
      content: f.content.trim() === "" ? "Content required" : ""
    }));
    setErrors(newErrors);
    return newErrors.every(err => !err.subject && !err.content);
  };

  /*followup sequence save button click handler */
  const handleFollowupSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = { emailTemplates: [...followUps] };
      await updateCampaignFollowups(selectedId, payload);

      setLoading(false);
      fetchFollowUpData();
      setSnackbar({ open: true, message: 'Follow-ups saved successfully!', severity: 'success' });
    } catch (err) {
      setLoading(false);
      if(err && err.message){
        setSnackbar({ open: true, message: err.message.message, severity: 'error' });
      }else{
        setSnackbar({ open: true, message: 'Failed to save follow-ups!', severity: 'error' });
      }
      //setSnackbar({ open: true, message: 'Failed to activate follow-ups!', severity: 'error' });
    }
  };

  if (initLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography variant="h5" fontWeight={600}>Follow-Up Sequence</Typography>
       {/* Content caption text */}
      <Typography variant="caption" fontWeight={400} color="textDisabled">
          Email sequence for followup
      </Typography>

      {/* Campaign dropdown */}
      <FormControl fullWidth sx={{ my: 2 }}>
        <Select
          value={selectedId || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          displayEmpty
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="">Select Campaign</MenuItem>
          {campaigns.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </Select>
        {!selectedId && <FormHelperText>Select a campaign to manage follow-ups</FormHelperText>}
      </FormControl>

      {/* Follow-up templates + button */}
      {selectedId && (
        <Grid container spacing={2}>
          {followUps.map((email, i) => (
            <Grid size={{xs:12, md:4}}  key={i}>
              <Card elevation={0} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Follow-Up Email #{i + 1}
                  </Typography>
                  <TextField
                    label="Subject"
                    fullWidth
                    size="small"
                    value={email.subject}
                    onChange={(e) => handleInputChange(i, "subject", e.target.value)}
                    error={!!errors[i]?.subject}
                    helperText={errors[i]?.subject}
                    sx={{ mb: 1 }}
                    slotProps={{ htmlInput: { maxLength: 100} }}
                  />
                  <TextField
                    label="Content"
                    fullWidth
                    multiline
                    rows={10}
                    value={email.content}
                    onChange={(e) => handleInputChange(i, "content", e.target.value)}
                    error={!!errors[i]?.content}
                    helperText={errors[i]?.content}
                    slotProps={{ htmlInput: { maxLength: 6000} }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Activate Button */}
          <Grid size={{xs:12, md:12}}>
            <Button
              variant="contained"
              fullWidth
              disabled={!followUps.every(f => f.subject.trim() && f.content.trim())}
              onClick={handleFollowupSave}
              loading={loading}
              loadingPosition="end"
            >
              Save Follow-Up Sequence
            </Button>
          </Grid>
        </Grid>
      )}
      {!selectedId && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <Stack direction="column">
            <CampaignRounded color="primary" sx={{ fontSize: 100, mx:10 }}/>
            <Typography variant="h4" color="textDisabled"> Select Campaign</Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
